/**
 * NotificationBell — polls unread count every 30s and renders a dropdown
 * with the 10 most recent notifications. Clicking a notification marks it
 * as read and (optionally) navigates to the linked entity.
 *
 * Backend endpoints:
 *   GET   /api/notifications?limit=10
 *   GET   /api/notifications/unread-count
 *   PATCH /api/notifications/:id/read
 *   POST  /api/notifications/mark-all-read
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const POLL_MS = 30000;

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setUnread(r.data?.unread ?? 0);
    } catch { /* silent — user might be mid-logout */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications?limit=10');
      setItems(r.data?.notifications || []);
    } catch { /* keep prev */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, [user, fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const onClickItem = async (n) => {
    // Optimistic UI: mark as read immediately
    if (!n.read_at) {
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString(), status: 'read' } : x));
      setUnread((u) => Math.max(0, u - 1));
      try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    const link = n.metadata?.link_url;
    if (link) {
      setOpen(false);
      navigate(link);
    }
  };

  const markAll = async () => {
    setMarking(true);
    try {
      await api.post('/notifications/mark-all-read');
      setItems((prev) => prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString(), status: 'read' })));
      setUnread(0);
    } finally { setMarking(false); }
  };

  const badge = unread > 99 ? '99+' : unread > 0 ? String(unread) : null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn" aria-label="Notifications">
          <Bell className="w-4.5 h-4.5" />
          {badge && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
            >
              {badge}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="notifications-menu">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="text-sm font-semibold">Notifications</div>
          <Button
            variant="ghost"
            size="sm"
            disabled={marking || unread === 0}
            onClick={markAll}
            data-testid="notifications-mark-all-btn"
            className="text-xs h-7 px-2"
          >
            {marking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5 mr-1" />}
            Mark all read
          </Button>
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="notifications-list">
          {loading ? (
            <div className="py-10 flex justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 px-6 flex flex-col items-center text-center gap-2 text-muted-foreground">
              <Sparkles className="w-5 h-5" />
              <p className="text-sm font-medium text-foreground">You&apos;re all caught up</p>
              <p className="text-xs">New activity across your workspace will show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onClickItem(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left px-3 py-3 hover:bg-muted/60 transition-colors flex gap-3 ${
                      !n.read_at ? 'bg-primary/5' : ''
                    }`}
                  >
                    <span
                      className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                        !n.read_at ? 'bg-primary' : 'bg-transparent border border-border'
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
                        {formatTime(n.created_at)} · {n.event_type}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffD = Math.floor(diffHr / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString();
}
